import { Text } from '@amzn/meridian';
import { type FormContextType, getUiOptions, type RJSFSchema, type StrictRJSFSchema, type WidgetProps } from '@rjsf/utils';
import { useCallback, useMemo } from 'react';
import type { MultiValue } from 'react-select';
import { type TagOption, TagSelect } from '../../../../../../../../components/form/TagSelect';
import { selectFormatterRegistry } from '../formatters';

/**
 * Custom RJSF widget for multi-select with create capability and bidirectional data formatting.
 *
 * This widget wraps the TagSelect component to provide a creatable multi-select interface
 * with support for custom data formatters. Formatters enable bidirectional transformation
 * between the form's storage format and the display format required by the select component.
 *
 * @example
 * // In JSON Schema:
 * {
 *   "tags": {
 *     "type": "string",
 *     "title": "Tags"
 *   }
 * }
 *
 * // In UI Schema (with formatter):
 * {
 *   "tags": {
 *     "ui:widget": "creatableSelect",
 *     "ui:options": {
 *       "formatters": "stringListFormatters",
 *       "enumOptions": [
 *         { "label": "Option 1", "value": "opt1" },
 *         { "label": "Option 2", "value": "opt2" }
 *       ]
 *     }
 *   }
 * }
 *
 * @see selectFormatterRegistry for available formatters
 */

export const CreatableSelect = <T = never, S extends StrictRJSFSchema = RJSFSchema, F extends FormContextType = never>(
  props: WidgetProps<T, S, F>,
) => {
  const { onChange, value, id, disabled, placeholder, label, rawErrors, uiSchema, required } = props;

  const { toDisplayValue, toFormDataValue } = useMemo(() => {
    const uiOptions = getUiOptions(uiSchema);
    return selectFormatterRegistry[uiOptions.formatters] || {};
  }, [
    uiSchema,
  ]);

  const selectOptions: TagOption[] = useMemo(
    () =>
      uiSchema
        ? (uiSchema['ui:options']?.enumOptions || []).map((opt: TagOption) => ({
            label: opt.label,
            value: opt.value,
          }))
        : [],
    [
      uiSchema,
    ],
  );

  const formattedValues: TagOption[] = useMemo(
    () =>
      toDisplayValue
        ? toDisplayValue(value)
        : (value || []).map((v: string) => ({
            label: v,
            value: v,
          })),
    [
      value,
      toDisplayValue,
    ],
  );

  // Update the selected values with the labels from the options if labels are different than values
  const selectedValues: TagOption[] = useMemo(
    () =>
      formattedValues.map((formattedOpt) => {
        const matchingOption = selectOptions.find((opt) => opt.value === formattedOpt.value);
        return matchingOption || formattedOpt;
      }),
    [
      formattedValues,
      selectOptions,
    ],
  );

  const _onChange = useCallback(
    (newValue: MultiValue<TagOption>) => {
      const values = newValue || [];
      const formattedValues = toFormDataValue ? toFormDataValue(values) : values.map((opt) => opt.value);
      onChange(formattedValues);
    },
    [
      onChange,
      toFormDataValue,
    ],
  );

  const hasErrors = !!(rawErrors && rawErrors.length > 0);
  const errorMessages = hasErrors
    ? rawErrors.map((err) => (err === 'Required' ? 'Required: At least one value must be selected' : err))
    : undefined;

  return (
    <>
      <Text htmlFor={id} id={`${id}-label`} tag="label">
        {label}
        {required && '*'}
      </Text>
      <TagSelect
        disabled={disabled}
        errors={errorMessages}
        inputId={id}
        invalid={hasErrors}
        isCreatable={true}
        isMulti
        onChange={_onChange}
        options={selectOptions}
        placeholder={placeholder || 'Select or create...'}
        value={selectedValues}
      />
    </>
  );
};
